import { useMemo, type DragEvent, type MouseEvent } from 'react';
import type { Topic, TopicInstance } from '../../../client/IvtHubClient';
import { PRIORITY_BG } from '../../../utils/priority';
import { notesToTier } from '../utils/topicPriorityCycle';

interface TopicCardProps {
  topic: Topic;
  editMode: boolean;
  locked: boolean;
  onAddInstance: (topic: Topic) => void;
  onDeleteTopic: (topic: Topic) => void;
  onCycleInstancePriority: (topic: Topic, inst: TopicInstance) => void;
  onDeleteInstance: (topic: Topic, inst: TopicInstance) => void;
  onDragStart?: (e: DragEvent<HTMLDivElement>, topic: Topic) => void;
  onDragOver?: (e: DragEvent<HTMLDivElement>) => void;
  onDrop?: (e: DragEvent<HTMLDivElement>, topic: Topic) => void;
}

const DOT_FILL = {
  strategic: '#ffe1ad',
  recommended: '#d3eeff',
  opportunistic: '#e7e7e7',
} as const;

export default function TopicCard({
  topic,
  editMode,
  locked,
  onAddInstance,
  onDeleteTopic,
  onCycleInstancePriority,
  onDeleteInstance,
  onDragStart,
  onDragOver,
  onDrop,
}: TopicCardProps) {
  const stop = (e: MouseEvent) => e.stopPropagation();

  const instances = topic.instances ?? [];

  const tierCounts = useMemo(() => {
    let strategic = 0;
    let recommended = 0;
    let opportunistic = 0;
    for (const inst of topic.instances ?? []) {
      const tier = notesToTier(inst.notes);
      if (tier === 'strategic') strategic++;
      else if (tier === 'recommended') recommended++;
      else opportunistic++;
    }
    return { strategic, recommended, opportunistic };
  }, [topic.instances]);

  return (
    <div
      className={
        'ivt-topic-card' +
        (editMode ? ' ivt-topic-card--editing' : '') +
        (locked ? ' ivt-topic-card--locked' : '')
      }
      draggable={editMode}
      onDragStart={(e) => onDragStart?.(e, topic)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop?.(e, topic)}
    >
      <div className={`ivt-topic-card__header${editMode ? ' ivt-topic-card__header--edit' : ''}`}>
        <div className="ivt-topic-card__header-top">
          <span className="ivt-topic-card__title">{topic.name}</span>
          {!editMode && (
            <span className="ivt-topic-card__count">{instances.length} Topics</span>
          )}
          {editMode && (
            <>
              <button
                type="button"
                className="ivt-topic-card__add-btn"
                title="添加 Sub Topic"
                disabled={locked}
                onClick={(e) => {
                  stop(e);
                  onAddInstance(topic);
                }}
              >
                +
              </button>
              <button
                type="button"
                className="ivt-topic-card__close-btn"
                title="删除 Topic"
                disabled={locked}
                onClick={(e) => {
                  stop(e);
                  onDeleteTopic(topic);
                }}
              >
                ✕
              </button>
            </>
          )}
        </div>
        {!editMode && (
          <div className="ivt-topic-card__legend">
            <span className="ivt-topic-card__legend-item">
              <span className="ivt-topic-card__legend-swatch ivt-topic-card__legend-swatch--strategic" />
              {tierCounts.strategic} Strategic
            </span>
            <span className="ivt-topic-card__legend-item">
              <span className="ivt-topic-card__legend-swatch ivt-topic-card__legend-swatch--recommended" />
              {tierCounts.recommended} Recommended
            </span>
            <span className="ivt-topic-card__legend-item">
              <span className="ivt-topic-card__legend-swatch ivt-topic-card__legend-swatch--opportunistic" />
              {tierCounts.opportunistic} Opportunistic
            </span>
          </div>
        )}
      </div>

      <div className="ivt-topic-card__body">
        {instances.length === 0 ? (
          <div className="ivt-topic-card__empty">
            {editMode ? '暂无 UC，点 + 添加' : '暂无 UC'}
          </div>
        ) : (
          instances.map((inst) => {
            const tier = notesToTier(inst.notes);
            return (
              <div
                key={`${inst.industry ?? ''}||${inst.subTopicName ?? ''}`}
                className={
                  'ivt-topic-card__instance' +
                  (editMode ? ' ivt-topic-card__instance--editing' : '')
                }
                style={{ background: PRIORITY_BG[tier] }}
                onClick={editMode ? (e) => {
                  stop(e);
                  if (locked) return;
                  onCycleInstancePriority(topic, inst);
                } : undefined}
              >
                <span className="ivt-topic-card__instance-text">
                  {inst.subTopicName}
                </span>
                {editMode && (
                  <span className="ivt-topic-card__priority-dots" onClick={(e) => {
                    stop(e);
                    if (locked) return;
                    onCycleInstancePriority(topic, inst);
                  }}>
                    <span
                      className={`ivt-topic-card__dot${tier === 'strategic' ? ' ivt-topic-card__dot--active' : ''}`}
                      style={{ background: DOT_FILL.strategic }}
                    />
                    <span
                      className={`ivt-topic-card__dot${tier === 'recommended' ? ' ivt-topic-card__dot--active' : ''}`}
                      style={{ background: DOT_FILL.recommended }}
                    />
                    <span
                      className={`ivt-topic-card__dot${tier === 'opportunistic' ? ' ivt-topic-card__dot--active' : ''}`}
                      style={{ background: DOT_FILL.opportunistic }}
                    />
                  </span>
                )}
                {editMode && (
                  <button
                    type="button"
                    className="ivt-topic-card__instance-del"
                    title="移除"
                    disabled={locked}
                    onClick={(e) => {
                      stop(e);
                      onDeleteInstance(topic, inst);
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
